import { Directive, ElementRef, HostListener, Input, OnInit, OnDestroy } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';

@Directive({
  selector: '[appNumberMask]'
})
export class NumberMaskDirective implements OnInit, OnDestroy {
  @Input() decimals: number = 0; // Quantidade de casas decimais (0 para inteiros)
  @Input() allowNegative: boolean = false;

  private previousValue: string = '';
  private valueChangesSubscription?: Subscription;
  private isInputting: boolean = false; // Flag para evitar loop

  constructor(
    private el: ElementRef,
    private control: NgControl
  ) {}

  ngOnInit(): void {
    // Formata o valor inicial se existir
    const initialValue = this.control.value;
    if (initialValue !== null && initialValue !== undefined && initialValue !== '') {
      setTimeout(() => this.formatValue(initialValue), 0);
    }

    // Observa mudanças no valor do FormControl (patchValue, setValue, etc).
    // O check !isInputting fica DENTRO do setTimeout: o DefaultValueAccessor dispara
    // valueChanges antes do @HostListener('input') desta diretiva, então no momento
    // da subscription isInputting ainda é false. Avaliando dentro do callback, quando
    // o timer dispara o onInput já executou e isInputting está true → formatValue é ignorado.
    this.valueChangesSubscription = this.control.valueChanges?.subscribe((value) => {
      if (value !== null && value !== undefined && value !== '') {
        setTimeout(() => {
          if (!this.isInputting) this.formatValue(value);
        }, 0);
      }
    });
  }

  ngOnDestroy(): void {
    this.valueChangesSubscription?.unsubscribe();
  }

  @HostListener('input', ['$event'])
  onInput(event: any): void {
    this.isInputting = true; // Marca que está digitando
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Remove tudo exceto números, vírgula e sinal negativo
    value = value.replace(/[^\d,\-]/g, '');

    // Trata sinal negativo
    if (!this.allowNegative) {
      value = value.replace(/-/g, '');
    } else {
      // Permite apenas um sinal negativo no início
      const hasNegative = value.startsWith('-');
      value = value.replace(/-/g, '');
      if (hasNegative) {
        value = '-' + value;
      }
    }

    // Remove vírgulas extras (mantém apenas a primeira se houver decimais)
    if (this.decimals > 0) {
      const parts = value.split(',');
      if (parts.length > 2) {
        value = parts[0] + ',' + parts.slice(1).join('');
      }
      // Limita casas decimais
      if (parts.length === 2 && parts[1].length > this.decimals) {
        parts[1] = parts[1].substring(0, this.decimals);
        value = parts.join(',');
      }
    } else {
      // Remove vírgulas se não aceita decimais
      value = value.replace(/,/g, '');
    }

    // Extrai o número puro (durante a digitação não aplicamos Intl com milhares —
    // isso recria input.value e joga o cursor para o início).
    const numericValue = this.parseValue(value);

    if (value === '' || value === '-') {
      this.control.control?.setValue(null, { emitEvent: false });
      input.value = value === '-' ? '-' : '';
    } else if (!isNaN(numericValue)) {
      this.control.control?.setValue(numericValue, { emitEvent: false });
      input.value = value;
    } else if (this.decimals > 0 && value.endsWith(',')) {
      // Permite estado intermediário: ",", "12,"
      this.control.control?.setValue(null, { emitEvent: false });
      input.value = value;
    } else {
      input.value = value;
    }

    setTimeout(() => {
      this.isInputting = false;
    }, 0);
  }

  @HostListener('blur', ['$event'])
  onBlur(event: any): void {
    const input = event.target as HTMLInputElement;
    const numericValue = this.control.value;

    if (numericValue !== null && numericValue !== undefined && numericValue !== '') {
      this.formatValue(numericValue, input);
    }
  }

  @HostListener('focus', ['$event'])
  onFocus(event: any): void {
    this.isInputting = false;
    const input = event.target as HTMLInputElement;
    const controlValue = this.control.value;

    if (controlValue !== null && controlValue !== undefined) {
      const numericValue = typeof controlValue === 'number'
        ? controlValue
        : this.parseValue(String(controlValue));

      if (!isNaN(numericValue)) {
        if (this.decimals > 0) {
          input.value = numericValue.toFixed(this.decimals).replace('.', ',');
        } else {
          input.value = numericValue.toString();
        }
      }
      // Seleciona tudo para que o usuário possa sobrescrever o valor com uma nova digitação.
      // Sem isso, o cursor fica no fim e dígitos vão para a casa decimal (ex: "0,00" + "5" → "0,005" → truncado).
      setTimeout(() => input.select(), 0);
    }
  }

  private parseValue(value: string): number {
    if (!value || value === '-') return 0;
    
    // Remove pontos de milhar e converte vírgula em ponto
    const cleanValue = value.replace(/\./g, '').replace(',', '.');
    return parseFloat(cleanValue);
  }

  private formatValue(value: any, input?: HTMLInputElement): void {
    const target = input || this.el.nativeElement;
    
    if (value === null || value === undefined || value === '') {
      target.value = '';
      return;
    }

    const numericValue = typeof value === 'number' ? value : this.parseValue(value.toString());

    if (isNaN(numericValue)) {
      target.value = '';
      return;
    }

    // Formata com separador de milhar e vírgula decimal
    const formatted = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: this.decimals,
      maximumFractionDigits: this.decimals,
      useGrouping: true
    }).format(numericValue);

    target.value = formatted;
  }
}
